import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { OfflineBar } from "@/components/OfflineBar";
import { ChunkBoundary } from "@/components/ChunkBoundary";
import Navbar from "@/components/Navbar";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import Categories from "@/pages/Categories";
import AppDetail from "@/pages/AppDetail";
import Developers from "@/pages/Developers";
import Feed from "@/pages/Feed";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Coins from "@/pages/Coins";
import Profile from "@/pages/Profile";
import DevPublish from "@/pages/DevPublish";
import DevDashboard from "@/pages/DevDashboard";
import AppAnalytics from "@/pages/AppAnalytics";
import AdminPanel from "@/pages/AdminPanel";
import Wallet from "@/pages/Wallet";
import SsoCallback from "@/pages/SsoCallback";
import NotificationSettings from "@/pages/NotificationSettings";
import GeneratedAppHost from "@/pages/GeneratedAppHost";
import JixxeoVibes from "@/embed/JixxeoVibes";
import JixxeoFlashlight from "@/embed/JixxeoFlashlight";
import JixxeoDraw from "@/embed/JixxeoDraw";
import JixxeoTimer from "@/embed/JixxeoTimer";
import JixxeoDice from "@/embed/JixxeoDice";
import JixxeoTicTacToe from "@/embed/JixxeoTicTacToe";
import JixxeoBlaster from "@/embed/JixxeoBlaster";
import JixxeoCube from "@/embed/JixxeoCube";
import JixxeoSnake3D from "@/embed/JixxeoSnake3D";
import JixxeoRacer from "@/embed/JixxeoRacer";
import JixxeoSwipeDodge from "@/embed/JixxeoSwipeDodge";
import JixxeoStackIt from "@/embed/JixxeoStackIt";
import JixxeoColorMatch from "@/embed/JixxeoColorMatch";
import JixxeoOneLine from "@/embed/JixxeoOneLine";
import JixxeoMemoryFlip from "@/embed/JixxeoMemoryFlip";
import JixxeoRPS from "@/embed/JixxeoRPS";
import JixxeoConnect4 from "@/embed/JixxeoConnect4";
import JixxeoDotTap from "@/embed/JixxeoDotTap";
import JixxeoReaction from "@/embed/JixxeoReaction";
import JixxeoXOBattle from "@/embed/JixxeoXOBattle";
import JixxeoGuessNumber from "@/embed/JixxeoGuessNumber";
import JixxeoEmojiDuel from "@/embed/JixxeoEmojiDuel";
import JixxeoTapRace from "@/embed/JixxeoTapRace";
import JixxeoCoinToss from "@/embed/JixxeoCoinToss";
import JixxeoJokeGenerator from "@/embed/JixxeoJokeGenerator";
import JixxeoTV from "@/embed/JixxeoTV";
import Roadmap from "@/pages/Roadmap";
import LoopWall from "@/pages/LoopWall";
import PowerLayout from "@/pages/power/PowerLayout";
import PowerHome from "@/pages/power/PowerHome";
import PowerExplore from "@/pages/power/PowerExplore";
import PowerCreate from "@/pages/power/PowerCreate";
import PowerPetition from "@/pages/power/PowerPetition";
import PowerNotifications from "@/pages/power/PowerNotifications";
import PowerProfile from "@/pages/power/PowerProfile";
import PowerWallet from "@/pages/power/PowerWallet";
import PowerRewards from "@/pages/power/PowerRewards";
import PowerReferrals from "@/pages/power/PowerReferrals";
import PowerLeaderboard from "@/pages/power/PowerLeaderboard";
import HandsLayout from "@/pages/hands/HandsLayout";
import HandsHome from "@/pages/hands/HandsHome";
import HandsAskAI from "@/pages/hands/HandsAskAI";
import HandsPlans from "@/pages/hands/HandsPlans";
import HandsCommunity from "@/pages/hands/HandsCommunity";
import HandsChallenges from "@/pages/hands/HandsChallenges";
import HandsBadges from "@/pages/hands/HandsBadges";
import MagicLayout from "@/pages/magic/MagicLayout";
import MagicHome from "@/pages/magic/MagicHome";
import MagicCreate from "@/pages/magic/MagicCreate";
import MagicGallery from "@/pages/magic/MagicGallery";
import MagicChallenge from "@/pages/magic/MagicChallenge";
import MagicFeed from "@/pages/magic/MagicFeed";
import SleepCalculator from "@/pages/sleep/SleepCalculator";
import WordUnscramblerApp from "@/pages/unscrambler/WordUnscramblerApp";
import TriviaApp from "@/pages/trivia/TriviaApp";
import TrashLayout from "@/pages/trash/TrashLayout";
import TrashHome from "@/pages/trash/TrashHome";
import TrashScan from "@/pages/trash/TrashScan";
import TrashMissions from "@/pages/trash/TrashMissions";
import TrashWallet from "@/pages/trash/TrashWallet";
import TrashLeaderboard from "@/pages/trash/TrashLeaderboard";
import TrashProfile from "@/pages/trash/TrashProfile";
import TrashLanding from "@/pages/trash/TrashLanding";
import ClickerLayout from "@/pages/clicker/ClickerLayout";
import ClickerPlay from "@/pages/clicker/ClickerPlay";
import ClickerUpgrades from "@/pages/clicker/ClickerUpgrades";
import ClickerBoost from "@/pages/clicker/ClickerBoost";
import ClickerStore from "@/pages/clicker/ClickerStore";
import ClickerRanks from "@/pages/clicker/ClickerRanks";
import WalkieLayout from "@/pages/walkie/WalkieLayout";
import TalkLayout from "@/pages/talk/TalkLayout";
import TalkChats from "@/pages/talk/TalkChats";
import TalkChat from "@/pages/talk/TalkChat";
import TalkCalls from "@/pages/talk/TalkCalls";
import TalkWallet from "@/pages/talk/TalkWallet";
import TalkMe from "@/pages/talk/TalkMe";
import TalkTop from "@/pages/talk/TalkTop";
import SpeedoApp from "@/pages/speedo/SpeedoApp";
import PlayZone from "@/pages/playzone/PlayZone";
import BalloonPop from "@/pages/playzone/BalloonPop";
import MirrorApp from "@/pages/mirror/MirrorApp";
import MirrorTv from "@/pages/mirror/MirrorTv";
import VibeLayout from "@/pages/vibe/VibeLayout";
import VibeFeed from "@/pages/vibe/VibeFeed";
import VibeCreate from "@/pages/vibe/VibeCreate";
import VibeProfile, { VibeMeRedirect, VibeSaved, VibeSound } from "@/pages/vibe/VibeProfile";
import VibeDiscover, { VibeTag, VibeWatch } from "@/pages/vibe/VibeDiscover";
import CloudLayout from "@/pages/cloud/CloudLayout";
import CloudGallery from "@/pages/cloud/CloudGallery";
import { CloudAlbums, CloudShared } from "@/pages/cloud/CloudPages";
import CloudSettings from "@/pages/cloud/CloudSettings";
import { CloudSharePage, CloudTv, CloudTvLink } from "@/pages/cloud/CloudPublic";
import WalkieHome from "@/pages/walkie/WalkieHome";
import WalkieChannels, { WalkieJoin } from "@/pages/walkie/WalkieChannels";
import WalkieRoom from "@/pages/walkie/WalkieRoom";
import WalkieLeaderboard from "@/pages/walkie/WalkieLeaderboard";
import WalkieNotifications from "@/pages/walkie/WalkieNotifications";
import WalkieProfile, { WalkieUser } from "@/pages/walkie/WalkieProfile";
import WalkieStore from "@/pages/walkie/WalkieStore";
import PublicPage from "@/pages/PublicPage";
import ContactPage from "@/pages/ContactPage";
import VoiceificationApp from "@/pages/voice/VoiceificationApp";
import VoiceBattles from "@/pages/voice/VoiceBattles";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { InstallBanner, UpdateToast } from "@/components/PwaInstall";
import { PushInlinePrompt } from "@/components/PushPanel";
import "@/App.css";

const CityCrusher = lazy(() => import("@/pages/playzone/arcade/CityCrusher"));
const WobbleTower = lazy(() => import("@/pages/playzone/arcade/WobbleTower"));
const VacuumMonster = lazy(() => import("@/pages/playzone/arcade/VacuumMonster"));
const LawnLegend = lazy(() => import("@/pages/playzone/arcade/LawnLegend"));
const PlanetCrash = lazy(() => import("@/pages/playzone/arcade/PlanetCrash"));
const BallEscape = lazy(() => import("@/pages/playzone/arcade/BallEscape"));
const LiveApp = lazy(() => import("@/pages/live/LiveApp"));
const SmashWorlds = lazy(() => import("@/pages/playzone/arcade/smash/SmashWorlds"));
const RiftSlice = lazy(() => import("@/pages/playzone/riftslice/RiftSlice"));
const RealityBreaker = lazy(() => import("@/pages/playzone/reality/RealityBreaker"));
const CrimeScene = lazy(() => import("@/pages/playzone/crime/CrimeScene"));
const NotebookApp = lazy(() => import("@/pages/notebook/NotebookApp"));
const MyJixxeo = lazy(() => import("@/components/home/Launcher"));
const VelouraApp = lazy(() => import("@/pages/veloura/VelouraApp"));

function Protected({ children, developerOnly }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="max-w-7xl mx-auto p-10 mono text-[#00F0FF] animate-pulse">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search + loc.hash)}`} replace />;
  if (developerOnly && !user.is_developer) return <Navigate to="/profile" replace />;
  return children;
}

function Shell() {
  const loc = useLocation();
  const navigate = useNavigate();

  // Listen for SW navigation intents fired from notificationclick
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (event) => {
      const data = event.data || {};
      if (data.type === "navigate" && data.link) {
        try {
          const u = new URL(data.link, window.location.origin);
          if (u.origin === window.location.origin) {
            navigate(u.pathname + u.search + u.hash);
            event.ports?.[0]?.postMessage("ok");
          }
        } catch {}
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [navigate]);

  // Jixxeo Voiceification — single-page voice-changer PWA
  if (loc.pathname.startsWith("/voiceification")) {
    return (
      <Routes>
        <Route path="/voiceification" element={<VoiceificationApp />} />
        <Route path="/voiceification/battles" element={<VoiceBattles />} />
      </Routes>
    );
  }

  // Jixxeo Helping Hands — dedicated shell (same iframe-friendly pattern as Power)
  if (loc.pathname.startsWith("/helpinghands")) {
    return (
      <Routes>
        <Route path="/helpinghands" element={<HandsLayout />}>
          <Route index element={<HandsHome />} />
          <Route path="ask" element={<HandsAskAI />} />
          <Route path="ask/:sid" element={<HandsAskAI />} />
          <Route path="plans" element={<HandsPlans />} />
          <Route path="community" element={<HandsCommunity />} />
          <Route path="challenges" element={<HandsChallenges />} />
          <Route path="badges" element={<HandsBadges />} />
        </Route>
      </Routes>
    );
  }

  // Jixxeo Magic — dedicated PWA chrome, no parent Navbar/Footer
  if (loc.pathname.startsWith("/magic")) {
    return (
      <Routes>
        <Route path="/magic" element={<MagicLayout />}>
          <Route index element={<MagicHome />} />
          <Route path="feed" element={<MagicFeed />} />
          <Route path="create/:style" element={<MagicCreate />} />
          <Route path="gallery" element={<MagicGallery />} />
          <Route path="challenge" element={<MagicChallenge />} />
        </Route>
      </Routes>
    );
  }

  // Jixxeo Sleep Calculator + Word Unscrambler — standalone PWA shells
  if (loc.pathname.startsWith("/sleep")) {
    return <Routes><Route path="/sleep" element={<SleepCalculator />} /></Routes>;
  }
  if (loc.pathname.startsWith("/unscrambler")) {
    return <Routes><Route path="/unscrambler" element={<WordUnscramblerApp />} /></Routes>;
  }
  if (loc.pathname.startsWith("/trivia")) {
    return <Routes><Route path="/trivia" element={<TriviaApp />} /></Routes>;
  }

  if (loc.pathname.startsWith("/g/")) {
    return <Routes><Route path="/g/:slug" element={<GeneratedAppHost />} /></Routes>;
  }
  if (loc.pathname.startsWith("/clicker")) {
    return (
      <Routes>
        <Route path="/clicker" element={<ClickerLayout />}>
          <Route index element={<ClickerPlay />} />
          <Route path="upgrades" element={<ClickerUpgrades />} />
          <Route path="boost" element={<ClickerBoost />} />
          <Route path="store" element={<ClickerStore />} />
          <Route path="ranks" element={<ClickerRanks />} />
        </Route>
      </Routes>
    );
  }
  if (loc.pathname === "/talk" || loc.pathname.startsWith("/talk/")) {
    return (
      <Routes>
        <Route path="/talk" element={<TalkLayout />}>
          <Route index element={<TalkChats />} />
          <Route path="c/:id" element={<TalkChat />} />
          <Route path="calls" element={<TalkCalls />} />
          <Route path="wallet" element={<TalkWallet />} />
          <Route path="me" element={<TalkMe />} />
          <Route path="top" element={<TalkTop />} />
        </Route>
      </Routes>
    );
  }
  if (loc.pathname === "/vibe" || loc.pathname.startsWith("/vibe/")) {
    return (
      <Routes>
        <Route path="/vibe" element={<VibeLayout />}>
          <Route index element={<VibeFeed />} />
          <Route path="discover" element={<VibeDiscover />} />
          <Route path="create" element={<VibeCreate />} />
          <Route path="saved" element={<VibeSaved />} />
          <Route path="me" element={<VibeMeRedirect />} />
          <Route path="u/:username" element={<VibeProfile />} />
          <Route path="sound/:id" element={<VibeSound />} />
          <Route path="tag/:tag" element={<VibeTag />} />
          <Route path="v/:id" element={<VibeWatch />} />
        </Route>
      </Routes>
    );
  }
  if (loc.pathname === "/cloud" || loc.pathname.startsWith("/cloud/")) {
    return (
      <Routes>
        <Route path="/cloud/tv" element={<CloudTv />} />
        <Route path="/cloud/s/:token" element={<CloudSharePage />} />
        <Route path="/cloud" element={<CloudLayout />}>
          <Route index element={<CloudGallery section="photos" />} />
          <Route path="videos" element={<CloudGallery section="videos" />} />
          <Route path="favorites" element={<CloudGallery section="favorites" />} />
          <Route path="trash" element={<CloudGallery section="trash" />} />
          <Route path="albums" element={<CloudAlbums />} />
          <Route path="albums/:id" element={<CloudGallery />} />
          <Route path="shared" element={<CloudShared />} />
          <Route path="settings" element={<CloudSettings />} />
          <Route path="tv/link" element={<CloudTvLink />} />
        </Route>
      </Routes>
    );
  }
  if (loc.pathname.startsWith("/playzone")) {
    return <Suspense fallback={<div className="fixed inset-0 pz-root flex items-center justify-center text-white/60 mono text-xs">Loading game…</div>}><Routes><Route path="/playzone" element={<PlayZone />} /><Route path="/playzone/balloon-pop" element={<BalloonPop />} />
      <Route path="/playzone/city-crusher" element={<CityCrusher />} /><Route path="/playzone/wobble-tower" element={<WobbleTower />} /><Route path="/playzone/vacuum-monster" element={<VacuumMonster />} /><Route path="/playzone/lawn-legend" element={<LawnLegend />} /><Route path="/playzone/planet-crash" element={<PlanetCrash />} /><Route path="/playzone/ball-escape" element={<BallEscape />} /><Route path="/playzone/smash-worlds" element={<SmashWorlds />} /><Route path="/playzone/riftslice" element={<RiftSlice />} /><Route path="/playzone/reality-breaker" element={<RealityBreaker />} /><Route path="/playzone/crime-scene" element={<CrimeScene />} /></Routes></Suspense>;
  }
  if (loc.pathname === "/veloura" || loc.pathname.startsWith("/veloura/")) {
    return <Suspense fallback={null}><VelouraApp /></Suspense>;
  }
  if (loc.pathname === "/notebook" || loc.pathname.startsWith("/notebook/")) {
    return <Suspense fallback={null}><Routes><Route path="/notebook/*" element={<NotebookApp />} /></Routes></Suspense>;
  }
  if (loc.pathname.startsWith("/speedometer")) {
    return <Routes><Route path="/speedometer" element={<SpeedoApp />} /></Routes>;
  }
  if (loc.pathname === "/mirror" || loc.pathname.startsWith("/mirror/")) {
    return <Routes><Route path="/mirror" element={<MirrorApp />} /><Route path="/mirror/tv" element={<MirrorTv />} /></Routes>;
  }
  if (loc.pathname.startsWith("/walkie")) {
    return (
      <Routes>
        <Route path="/walkie" element={<WalkieLayout />}>
          <Route index element={<WalkieHome />} />
          <Route path="channels" element={<WalkieChannels />} />
          <Route path="c/:id" element={<WalkieRoom />} />
          <Route path="join/:code" element={<WalkieJoin />} />
          <Route path="leaderboard" element={<WalkieLeaderboard />} />
          <Route path="notifications" element={<WalkieNotifications />} />
          <Route path="me" element={<WalkieProfile />} />
          <Route path="u/:uid" element={<WalkieUser />} />
          <Route path="store" element={<WalkieStore />} />
        </Route>
      </Routes>
    );
  }

  // Jixxeo Trash Hunter — dedicated PWA chrome
  if (loc.pathname.startsWith("/trashhunter")) {
    return (
      <Routes>
        <Route path="/trashhunter" element={<TrashLayout />}>
          <Route index element={<TrashHome />} />
          <Route path="landing" element={<TrashLanding />} />
          <Route path="scan" element={<TrashScan />} />
          <Route path="missions" element={<TrashMissions />} />
          <Route path="wallet" element={<TrashWallet />} />
          <Route path="leaderboard" element={<TrashLeaderboard />} />
          <Route path="me" element={<TrashProfile />} />
        </Route>
      </Routes>
    );
  }

  // Jixxeo Power — dedicated PWA chrome, no parent Navbar/Footer
  if (loc.pathname.startsWith("/power")) {
    return (
      <Routes>
        <Route path="/power" element={<PowerLayout />}>
          <Route index element={<PowerHome />} />
          <Route path="home" element={<Navigate to="/power" replace />} />
          <Route path="explore" element={<PowerExplore />} />
          <Route path="create" element={<PowerCreate />} />
          <Route path="notifications" element={<PowerNotifications />} />
          <Route path="profile" element={<PowerProfile />} />
          <Route path="wallet" element={<PowerWallet />} />
          <Route path="rewards" element={<PowerRewards />} />
          <Route path="referrals" element={<PowerReferrals />} />
          <Route path="leaderboard" element={<PowerLeaderboard />} />
          <Route path="p/:slug" element={<PowerPetition />} />
        </Route>
      </Routes>
    );
  }

  // Embedded apps render without the Jixxeo chrome (they run inside the launcher iframe)
  if (loc.pathname.startsWith("/embed/")) {
    return (
      <Routes>
        <Route path="/embed/vibes" element={<JixxeoVibes />} />
        <Route path="/embed/flashlight" element={<JixxeoFlashlight />} />
        <Route path="/embed/draw" element={<JixxeoDraw />} />
        <Route path="/embed/timer" element={<JixxeoTimer />} />
        <Route path="/embed/dice" element={<JixxeoDice />} />
        <Route path="/embed/tictactoe" element={<JixxeoTicTacToe />} />
        <Route path="/embed/blaster" element={<JixxeoBlaster />} />
        <Route path="/embed/cube" element={<JixxeoCube />} />
        <Route path="/embed/snake3d" element={<JixxeoSnake3D />} />
        <Route path="/embed/racer" element={<JixxeoRacer />} />
        <Route path="/embed/swipe-dodge" element={<JixxeoSwipeDodge />} />
        <Route path="/embed/stack-it" element={<JixxeoStackIt />} />
        <Route path="/embed/color-match" element={<JixxeoColorMatch />} />
        <Route path="/embed/one-line" element={<JixxeoOneLine />} />
        <Route path="/embed/memory-flip" element={<JixxeoMemoryFlip />} />
        <Route path="/embed/rps" element={<JixxeoRPS />} />
        <Route path="/embed/connect4" element={<JixxeoConnect4 />} />
        <Route path="/embed/dot-tap" element={<JixxeoDotTap />} />
        <Route path="/embed/reaction" element={<JixxeoReaction />} />
        <Route path="/embed/xo-battle" element={<JixxeoXOBattle />} />
        <Route path="/embed/guess-number" element={<JixxeoGuessNumber />} />
        <Route path="/embed/emoji-duel" element={<JixxeoEmojiDuel />} />
        <Route path="/embed/tap-race" element={<JixxeoTapRace />} />
        <Route path="/embed/coin-toss" element={<JixxeoCoinToss />} />
        <Route path="/embed/joke-generator" element={<JixxeoJokeGenerator />} />
        <Route path="/embed/jixxeotv" element={<JixxeoTV />} />
      </Routes>
    );
  }
  return (
    <div className="min-h-screen text-white relative">
      <Navbar />
      <div className="relative z-10">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/loops" element={<LoopWall />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/category/:id" element={<Browse />} />
          <Route path="/app/:slug" element={<AppDetail />} />
          <Route path="/developers" element={<Developers />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/coins" element={<Protected><Coins /></Protected>} />
          <Route path="/my-jixxeo" element={<Suspense fallback={null}><MyJixxeo /></Suspense>} />
          <Route path="/live" element={<Protected><Suspense fallback={null}><LiveApp /></Suspense></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/dev/publish" element={<Protected><DevPublish /></Protected>} />
          <Route path="/dev/apps" element={<Protected developerOnly><DevDashboard /></Protected>} />
          <Route path="/dev/apps/:slug/analytics" element={<Protected developerOnly><AppAnalytics /></Protected>} />
          <Route path="/earn" element={<Protected><Wallet /></Protected>} />
          <Route path="/sso/callback" element={<SsoCallback />} />
          <Route path="/settings/notifications" element={<Protected><NotificationSettings /></Protected>} />
          <Route path="/admin" element={<Protected><AdminPanel /></Protected>} />
          <Route path="/privacy" element={<PublicPage />} />
          <Route path="/terms" element={<PublicPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/roadmap" element={<Roadmap />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ChunkBoundary><Shell /></ChunkBoundary>
        <InstallBanner />
        <PushInlinePrompt />
        <UpdateToast />
        <OfflineBar />
      </AuthProvider>
      <Toaster theme="dark" position="bottom-right" toastOptions={{
        style: { background: "#0A0A0E", color: "#fff", border: "1px solid rgba(0,240,255,0.3)" }
      }}/>
    </BrowserRouter>
  );
}

export default App;
